import { useState, useCallback } from 'react';
import userService from '../services/userService';

export const useUserManagement = () => {
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState({
    username: '',
    role: 'all',
    status: 'all'
  });

  // Función para aplicar filtros
  const applyFilters = useCallback((userList, currentFilters) => {
    return userList.filter(user => {
      // Filtro por username
      if (currentFilters.username && !user.username.toLowerCase().includes(currentFilters.username.toLowerCase())) {
        return false;
      }
      
      // Filtro por rol
      if (currentFilters.role !== 'all' && user.role !== currentFilters.role) {
        return false;
      }
      
      // Filtro por estado
      if (currentFilters.status !== 'all') {
        const isActive = currentFilters.status === 'active';
        if (user.isActive !== isActive) {
          return false;
        }
      }
      
      return true;
    });
  }, []);

  // Obtener todos los usuarios
  const fetchUsers = useCallback(async () => {
    setLoading(true);
    setError(null);
    
    try {
      const data = await userService.getUsers();
      setUsers(data);
      setFilteredUsers(applyFilters(data, filters));
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [filters, applyFilters]);

  // Buscar usuario por username
  const searchUserByUsername = useCallback(async (username) => {
    setLoading(true);
    setError(null);
    
    try {
      const data = await userService.getUserByUsername(username);
      setUsers([data]);
      setFilteredUsers([data]);
      return data;
    } catch (err) {
      setError(err.message);
      setUsers([]);
      setFilteredUsers([]);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  // Aplicar filtros
  const applyUserFilters = useCallback((newFilters) => {
    setFilters(newFilters);
    // Solo aplicar filtros si no es solo un cambio en username
    // o si se está aplicando explícitamente
    if (newFilters.applyNow) {
      const { applyNow, ...filtersToApply } = newFilters;
      setFilteredUsers(applyFilters(users, filtersToApply));
    }
  }, [users, applyFilters]);

  // Aplicar filtros explícitamente (cuando se presiona el botón)
  const applyFiltersNow = useCallback(() => {
    setFilteredUsers(applyFilters(users, filters));
  }, [users, filters, applyFilters]);

  // Limpiar filtros
  const clearFilters = useCallback(() => {
    const defaultFilters = {
      username: '',
      role: 'all',
      status: 'all'
    };
    setFilters(defaultFilters);
    setFilteredUsers(users);
  }, [users]);

  // Crear usuario
  const createUser = useCallback(async (userData) => {
    setLoading(true);
    setError(null);
    
    try {
      const newUser = await userService.createUser(userData);
      const updatedUsers = [...users, newUser];
      setUsers(updatedUsers);
      setFilteredUsers(applyFilters(updatedUsers, filters));
      return newUser;
    } catch (err) {
      // NO establecer el error global aquí
      // Solo lanzar la excepción para que el modal la maneje
      throw err;
    } finally {
      setLoading(false);
    }
  }, [users, filters, applyFilters]);

  // Actualizar usuario
  const updateUser = useCallback(async (id, userData) => {
    setLoading(true);
    setError(null);
    
    try {
      const updatedUser = await userService.updateUser(id, userData);
      const updatedUsers = users.map(user => 
        user.id === id ? updatedUser : user
      );
      setUsers(updatedUsers);
      setFilteredUsers(applyFilters(updatedUsers, filters));
      
      // Actualizar usuario seleccionado si es el mismo
      if (selectedUser?.id === id) {
        setSelectedUser(updatedUser);
      }
      
      return updatedUser;
    } catch (err) {
      // NO establecer el error global aquí
      // Solo lanzar la excepción para que el modal la maneje
      throw err;
    } finally {
      setLoading(false);
    }
  }, [users, filters, selectedUser, applyFilters]);

  // Eliminar usuario
  const deleteUser = useCallback(async (id) => {
    setLoading(true);
    setError(null);
    
    try {
      await userService.deleteUser(id);
      const updatedUsers = users.filter(user => user.id !== id);
      setUsers(updatedUsers);
      setFilteredUsers(applyFilters(updatedUsers, filters));
      
      // Limpiar usuario seleccionado si es el mismo
      if (selectedUser?.id === id) {
        setSelectedUser(null);
      }
      
      return true;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [users, filters, selectedUser, applyFilters]);

  // Limpiar error
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  // Limpiar usuario seleccionado
  const clearSelectedUser = useCallback(() => {
    setSelectedUser(null);
  }, []);

  // Limpiar búsqueda y mostrar todos los usuarios
  const clearSearch = useCallback(() => {
    fetchUsers();
  }, [fetchUsers]);

  return {
    // Estado
    users: filteredUsers, // Retornar usuarios filtrados
    allUsers: users, // Usuarios sin filtrar
    selectedUser,
    loading,
    error,
    filters,
    
    // Acciones
    fetchUsers,
    searchUserByUsername,
    applyUserFilters,
    applyFiltersNow, // Nuevo botón de aplicar filtros
    clearFilters,
    createUser,
    updateUser,
    deleteUser,
    clearError,
    clearSelectedUser,
    clearSearch,
    setSelectedUser,
    setUsers
  };
}; 